import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputField } from '../../../../../../components/atoms/input-field';

import { useUpdateDestinationTranslationsDialogValidationSchema } from './schema/update-destination-translations-dialog-validation-schema';

import { Dialog, DialogActions as DialogActionsInterface } from '@/components/molecules/dialog';
import { DestinationTranslations } from '@/features/destinations/api/types/destination';
import { useUpdateDestination } from '@/features/destinations/hooks/mutation/use-update-destination';

interface Props extends DialogActionsInterface {
  destinationId: number;
  translations: DestinationTranslations;
}

interface FormValues {
  pl?: string;
  en?: string;
  ua?: string;
}

export const UpdateDestinationTranslationsDialog = ({
  destinationId,
  translations,
  open,
  onCancel,
}: Props) => {
  const { t } = useTranslation(['destination', 'common']);

  const validationSchema = useUpdateDestinationTranslationsDialogValidationSchema();

  const defaultValues: FormValues = {
    pl: translations?.pl || '',
    en: translations?.en || '',
    ua: translations?.ua || '',
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(defaultValues);
  }, [open, reset, translations]);

  const { mutate, isPending } = useUpdateDestination(onCancel);

  const onSubmit = (data: FormValues) => {
    const translations = {
      pl: data.pl || undefined,
      en: data.en || undefined,
      ua: data.ua || undefined,
    };

    mutate({
      payload: { translations },
      destinationId,
    });
  };

  return (
    <Dialog
      title={t('destination:updateTranslationsDialogTitle')}
      onCancel={onCancel}
      open={open}
      confirmButtonText={t('common:button.edit')}
      isConfirmButtonDisabled={!isValid || !isDirty}
      isConfirmButtonSubmitting={isPending}
      onConfirm={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="pl"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                fullWidth
                label={t('destination:translations.polish')}
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="en"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                fullWidth
                label={t('destination:translations.english')}
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="ua"
            render={({ field, fieldState: { error } }) => (
              <InputField
                {...field}
                fullWidth
                label={t('destination:translations.ukrainian')}
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
      </Grid>
    </Dialog>
  );
};
